import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PageSeo from '../../components/PageSeo';
import { 
  ChevronRight, Building2, MapPin, ArrowLeft, Phone, Mail, Globe, 
  Search, Award, Filter, Users, ExternalLink
} from 'lucide-react';

// Generate URL-friendly slug from company name and city
function generateSlug(companyName, city) {
  let text = companyName || '';
  if (city) {
    text = `${companyName}-${city}`;
  }
  
  // Normalize unicode characters (é → e, etc.)
  text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  // Convert to lowercase
  text = text.toLowerCase();
  
  // Replace spaces and special chars with hyphens
  text = text.replace(/[^a-z0-9]+/g, '-');
  
  // Remove leading/trailing hyphens and duplicate hyphens
  text = text.replace(/^-+|-+$/g, '').replace(/-+/g, '-');
  
  return text;
}

// Generate initials from company name
function getInitials(companyName) {
  if (!companyName) return '??';
  const words = companyName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Company Logo component with error fallback to initials
function CompanyLogoCard({ logo, companyName, featured, API }) {
  const [imageError, setImageError] = useState(false);
  
  const initials = getInitials(companyName);
  
  // Show initials if no logo or if image failed to load
  if (!logo || imageError) {
    return (
      <div className="w-16 h-16 rounded-lg flex items-center justify-center text-lg font-bold bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] text-white">
        {initials}
      </div>
    );
  }
  
  // Build the logo URL - handle different cases
  let logoUrl = logo;
  if (logo.startsWith('http')) {
    // Full URL - use as is
    logoUrl = logo;
  } else if (logo.startsWith('/api/')) {
    // Already has /api prefix - use base URL without /api
    const baseUrl = API.replace(/\/api$/, '');
    logoUrl = `${baseUrl}${logo}`;
  } else if (logo.startsWith('/')) {
    // Relative path without /api - add API base
    logoUrl = `${API}${logo}`;
  } else {
    // No leading slash - add API base with slash
    logoUrl = `${API}/${logo}`;
  }
  
  return (
    <img 
      src={logoUrl} 
      alt={companyName}
      className="w-16 h-16 object-contain rounded-lg bg-white p-2"
      onError={() => setImageError(true)}
    />
  );
}

export default function DirectoryCategory() {
  const { familySlug, categorySlug } = useParams();
  const { API } = useContext(AppContext);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({
    country: 'all',
    city: '',
    search: ''
  });

  // Fetch category with professionals
  const fetchCategory = async () => {
    try {
      setLoading(true);
      let url = `${API}/directory/families/${familySlug}/${categorySlug}`;
      const params = [];
      
      if (filters.country !== 'all') params.push(`country=${filters.country}`);
      if (filters.city) params.push(`city=${encodeURIComponent(filters.city)}`);
      if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
      
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      setCategory(response.data);
    } catch (err) {
      console.error('Error fetching category:', err);
      setError('Catégorie non trouvée');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, [API, familySlug, categorySlug, filters]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API}/directory/countries`);
        setCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, [API]);

  // Track views for professionals
  useEffect(() => {
    if (category?.professionals) {
      category.professionals.forEach(async (pro) => {
        try {
          await axios.post(`${API}/directory/professionals/${pro.id}/track-view`);
        } catch (error) {
          // Silent fail for tracking
        }
      });
    }
  }, [category?.professionals, API]);

  if (loading && !category) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF]"></div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{error || 'Catégorie non trouvée'}</h1>
          <Link to="/annuaire">
            <Button>Retour à l'annuaire</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageSeo 
        title={category.meta_title_fr || `${category.name_fr} | Annuaire GEOTS`}
        description={category.meta_description_fr || category.description_fr}
        canonical={`/annuaire/${familySlug}/${categorySlug}`}
      />
      
      {/* Breadcrumb & Hero */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6 text-gray-300">
            <Link to="/annuaire" className="hover:text-white flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Annuaire
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/annuaire/${familySlug}`} className="hover:text-white">
              {category.family_name_fr}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">{category.name_fr}</span>
          </nav>
          
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {category.name_fr}
            </h1>
            <p className="text-lg text-gray-300 mb-6">
              {category.description_fr}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Users className="w-5 h-5 text-green-400" />
                <span>{category.professional_count || 0} entreprise{(category.professional_count || 0) !== 1 ? 's' : ''}</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <MapPin className="w-5 h-5 text-blue-400" />
                <span>France & Afrique</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700">Filtrer les résultats</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Rechercher une entreprise..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </div>

              {/* Country */}
              <Select 
                value={filters.country} 
                onValueChange={(value) => setFilters({...filters, country: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les pays</SelectItem>
                  {countries.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* City */}
              <Input
                type="text"
                placeholder="Ville..."
                value={filters.city}
                onChange={(e) => setFilters({...filters, city: e.target.value})}
              />
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            <span className="font-semibold text-[#0A2540]">{category.professionals?.length || 0}</span> entreprise{(category.professionals?.length || 0) !== 1 ? 's' : ''} trouvée{(category.professionals?.length || 0) !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse bg-gray-200 rounded-xl h-72"></div>
            ))}
          </div>
        ) : category.professionals?.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aucune entreprise trouvée
            </h3>
            <p className="text-gray-500 mb-6">
              Modifiez vos filtres ou soyez le premier à vous inscrire dans cette catégorie.
            </p>
            <Link to="/directory/submit">
              <Button className="bg-[#635BFF] hover:bg-[#5248d9]">
                Inscrire mon entreprise
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.professionals.map(pro => {
              const companySlug = pro.slug || generateSlug(pro.company_name, pro.city);
              const companyUrl = `/annuaire/${categorySlug}/${companySlug}`;
              
              return (
                <Link 
                  key={pro.id} 
                  to={companyUrl}
                  className="block"
                >
                  <Card 
                    className={`group hover:shadow-xl transition-all duration-300 cursor-pointer h-full ${
                      pro.featured ? 'border-2 border-yellow-400 bg-gradient-to-b from-yellow-50 to-white' : 'border hover:border-[#635BFF]'
                    }`}
                  >
                    <CardContent className="p-0">
                      {/* Header */}
                      <div className={`p-6 ${
                        pro.featured 
                          ? 'bg-gradient-to-r from-[#0A2540] to-[#1a3a5c] text-white' 
                          : 'bg-gray-50 border-b'
                      }`}>
                        {pro.featured && (
                          <span className="inline-block bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold mb-3">
                            PREMIUM
                          </span>
                        )}
                        
                        <div className="flex items-start gap-4">
                          <CompanyLogoCard 
                            logo={pro.logo} 
                            companyName={pro.company_name}
                            featured={pro.featured}
                            API={API}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-lg font-bold truncate group-hover:underline ${
                              pro.featured ? 'text-white' : 'text-[#0A2540]'
                            }`}>
                              {pro.company_name}
                            </h3>
                            <div className={`flex items-center text-sm mt-1 ${
                              pro.featured ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                              <span className="truncate">{pro.city}, {pro.country}</span>
                            </div>
                          </div>
                          <ExternalLink className={`w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
                            pro.featured ? 'text-white' : 'text-[#635BFF]'
                          }`} />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {pro.description_fr || 'Entreprise spécialisée dans ce domaine.'}
                        </p>

                        {/* Certifications */}
                        {pro.certifications && pro.certifications.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {pro.certifications.slice(0, 3).map((cert, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                                <Award className="w-3 h-3" />
                                {cert}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Quick info preview */}
                        <div className="space-y-2 border-t pt-4 text-sm text-gray-500">
                          {(pro.phone || pro.contact_phone) && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{pro.phone || pro.contact_phone}</span>
                            </div>
                          )}
                          {pro.website && (
                            <div className="flex items-center">
                              <Globe className="w-4 h-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{pro.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                            </div>
                          )}
                        </div>

                        {/* View profile CTA */}
                        <div className="mt-4 pt-4 border-t">
                          <span className="inline-flex items-center gap-2 text-[#635BFF] font-medium text-sm group-hover:underline">
                            Voir la fiche complète
                            <ExternalLink className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* SEO Content Section */}
      {category.seo_content_fr && (
        <div className="bg-white border-t">
          <div className="container mx-auto px-4 py-12">
            <div className="prose prose-lg max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: category.seo_content_fr }} />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] py-12">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Votre entreprise propose ces services ?
          </h2>
          <p className="text-lg text-white/80 mb-6 max-w-xl mx-auto">
            Inscrivez-vous gratuitement et apparaissez dans cette catégorie pour être trouvé par les donneurs d'ordre.
          </p>
          <Link to="/directory/submit">
            <Button size="lg" className="bg-white text-[#635BFF] hover:bg-gray-100">
              Inscrire mon entreprise gratuitement
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
